/**
 * Format an integer-minor-units amount for display.
 * Locale-aware — symbol placement, decimal separator, grouping all
 * follow the currency's convention (e.g. INR uses lakh grouping;
 * JPY has zero decimals).
 *
 * @param minor  Integer minor units (e.g. 1234 = $12.34 for USD).
 * @param currency  ISO 4217 code from workspace.settings.currency.
 * @param opts.locale  Override locale (defaults to navigator.language).
 * @param opts.compact  Use compact notation (e.g. "$1.2K").
 * @param opts.workspaceId  Optional workspace ID for warning logging.
 */
export const formatMoney = (
  minor: number,
  currency?: string | null,
  opts?: { locale?: string; compact?: boolean; workspaceId?: string },
): string => {
  const safeMinor = Number.isFinite(minor) ? minor : 0
  const normalized = typeof currency === 'string' ? currency.trim().toUpperCase() : ''
  let targetCurrency = normalized

  if (!/^[A-Z]{3}$/.test(targetCurrency)) {
    const wsLabel = opts?.workspaceId ? `Workspace ${opts.workspaceId}` : 'Workspace'
    console.warn(
      `[formatMoney] ${wsLabel} has no valid currency set (got "${currency}") — display fell back to USD.`,
    )
    targetCurrency = 'USD'
  }

  const locale =
    opts?.locale || (typeof navigator !== 'undefined' ? navigator.language : undefined)

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: targetCurrency,
  }

  if (opts?.compact) {
    formatOptions.notation = 'compact'
  }

  try {
    return new Intl.NumberFormat(locale, formatOptions).format(safeMinor / 100)
  } catch (err) {
    console.warn(
      `[formatMoney] Intl.NumberFormat failed for currency "${targetCurrency}" with locale "${locale}" — falling back to USD.`,
      err,
    )
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        ...(opts?.compact ? { notation: 'compact' } : {}),
      }).format(safeMinor / 100)
    } catch {
      return `USD ${(safeMinor / 100).toFixed(2)}`
    }
  }
}
